import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus, Trash2, ChevronDown, ChevronRight, Edit2, Check, X, GripVertical, Copy, MoreVertical, Minimize2, Maximize2, List, ListTree } from 'lucide-react';
import { produce } from 'immer';
import { toast } from 'sonner';
import Button from './Button';
import ContextMenu from './ContextMenu';
import FloatingSaveButton from './FloatingSaveButton';
import FloatingUndoButton from './FloatingUndoButton';
import ScriptNavigator from './ScriptNavigator';
import BotRoleEditor from './BotRoleEditor';
import SettingsEditor from './SettingsEditor';
import IncidentsEditor from './IncidentsEditor';
import FollowupsEditor from './FollowupsEditor';
import StepVarEditor from './StepVarEditor';
import CoordinatingSettingsEditor from './CoordinatingSettingsEditor';
import AdvancedJsonEditor from './AdvancedJsonEditor';
import useUndoRedo from '../hooks/useUndoRedo';
import { 
  createEmptyStage, 
  createEmptyStageVar, 
  createEmptyStep, 
  createEmptyStepVar,
  cloneStage,
  cloneStep,
  getNextStageNumber,
  getNextStepNumber,
  validateCharLimit
} from '../lib/scriptHelpers';

// Сортируемый Stage компонент
function SortableStage({ stage, index, children, onContextMenu }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: stage.UUID });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="relative group"
        onContextMenu={(e) => {
          e.preventDefault();
          onContextMenu(e, stage, index);
        }}
      >
        <div className="absolute -left-8 top-4 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing" {...listeners} {...attributes}>
          <GripVertical className="text-gray-400" size={20} />
        </div>
        {children}
      </motion.div>
    </div>
  );
}

// Сортируемый Step компонент
function SortableStep({ step, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step.UUID });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group">
      <div className="absolute -left-6 top-3 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing" {...listeners} {...attributes}>
        <GripVertical className="text-gray-400" size={16} />
      </div>
      {children}
    </div>
  );
}

const ImprovedScriptEditor = React.forwardRef(({ script: initialScript, onChange, onSave }, ref) => {
  // Правильная инициализация с корректными типами данных (согласно структуре БД)
  const defaultScript = {
    Settings: {
      Bot_Role: [],
      Notifications: {
        Managers: [],
        All_Statuses: {},
        Custom_CRM_Settings: {},
        Notification_Settings: []
      },
      Telegram_Channel: ''
    },
    Coordinating_Settings: [],
    Main_Script: { Stages: [] },
    Script_For_Incidents: { Incident_Groups: [] },
    Followups_For_Scripts: { Followup_Groups: [] }
  };
  
  const { state: script, updateState, undo, redo, canUndo, canRedo } = useUndoRedo(initialScript || defaultScript);
  
  const [expandedStages, setExpandedStages] = useState({});
  const [expandedVariants, setExpandedVariants] = useState({});
  const [expandedSteps, setExpandedSteps] = useState({});
  const [detailLevel, setDetailLevel] = useState(null);
  const [editingStep, setEditingStep] = useState({ key: null, name: '', description: '' });

  const startEditStep = (e, stepKey, step) => {
    e.stopPropagation();
    setEditingStep({ key: stepKey, name: step.Step_Name || '', description: step.Step_Description || '' });
  };

  const saveEditStep = (stageIndex, variantIndex, stepIndex) => {
    updateStepField(stageIndex, variantIndex, stepIndex, 'Step_Name', editingStep.name);
    updateStepField(stageIndex, variantIndex, stepIndex, 'Step_Description', editingStep.description);
    setEditingStep({ key: null, name: '', description: '' });
  };

  const cancelEditStep = () => {
    setEditingStep({ key: null, name: '', description: '' });
  };

  const [editingJson, setEditingJson] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [contextMenu, setContextMenu] = useState({ isOpen: false, position: { x: 0, y: 0 }, item: null, index: null });
  const [currentNavigationUUID, setCurrentNavigationUUID] = useState(null);

  // Инициализация пустых полей при первом рендере
  useEffect(() => {
    if (script) {
      let needsUpdate = false;
      const updates = {};

      // Проверяем Settings
      if (!script.Settings) {
        updates.Settings = {
          Bot_Role: [],
          Notifications: {
            Managers: [],
            All_Statuses: {},
            Custom_CRM_Settings: {},
            Notification_Settings: []
          },
          Telegram_Channel: ''
        };
        needsUpdate = true;
      } else {
        // Проверяем Notifications (должен быть объект, не массив)
        if (!script.Settings.Notifications || Array.isArray(script.Settings.Notifications) || typeof script.Settings.Notifications !== 'object') {
          updates.Settings = {
            ...script.Settings,
            Notifications: {
              Managers: [],
              All_Statuses: {},
              Custom_CRM_Settings: {},
              Notification_Settings: []
            }
          };
          needsUpdate = true;
        } else {
          // Проверяем вложенные поля Notifications
          const notif = script.Settings.Notifications;
          if (!notif.All_Statuses || typeof notif.All_Statuses !== 'object' || Array.isArray(notif.All_Statuses)) {
            updates.Settings = {
              ...script.Settings,
              Notifications: {
                ...notif,
                All_Statuses: {}
              }
            };
            needsUpdate = true;
          }
        }
      }

      // Проверяем Coordinating_Settings
      if (!script.Coordinating_Settings || !Array.isArray(script.Coordinating_Settings)) {
        updates.Coordinating_Settings = [];
        needsUpdate = true;
      }

      // Проверяем другие обязательные поля (должны быть объектами с массивами)
      if (!script.Script_For_Incidents || typeof script.Script_For_Incidents !== 'object' || Array.isArray(script.Script_For_Incidents)) {
        updates.Script_For_Incidents = { Incident_Groups: [] };
        needsUpdate = true;
      } else if (!script.Script_For_Incidents.Incident_Groups || !Array.isArray(script.Script_For_Incidents.Incident_Groups)) {
        updates.Script_For_Incidents = { ...script.Script_For_Incidents, Incident_Groups: [] };
        needsUpdate = true;
      }

      if (!script.Followups_For_Scripts || typeof script.Followups_For_Scripts !== 'object' || Array.isArray(script.Followups_For_Scripts)) {
        updates.Followups_For_Scripts = { Followup_Groups: [] };
        needsUpdate = true;
      } else if (!script.Followups_For_Scripts.Followup_Groups || !Array.isArray(script.Followups_For_Scripts.Followup_Groups)) {
        updates.Followups_For_Scripts = { ...script.Followups_For_Scripts, Followup_Groups: [] };
        needsUpdate = true;
      }

      if (needsUpdate) {
        updateState({ ...script, ...updates });
      }
    }
  }, []);
  const [activeTab, setActiveTab] = useState('main_script');
  
  // Отслеживание последнего сохраненного состояния
  const lastSavedStateRef = useRef(JSON.stringify(initialScript));
  
  // Expose метод для получения текущего скрипта (включая JSON)
  React.useImperativeHandle(ref, () => ({
    getCurrentScript: () => {
      if (editingJson) {
        try {
          return JSON.parse(jsonText);
        } catch (err) {
          console.error('Invalid JSON:', err);
          toast.error('Невалидный JSON. Проверьте синтаксис.');
          return null;
        }
      }
      return script;
    }
  }));
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Отслеживание изменений относительно последнего сохраненного состояния
  useEffect(() => {
    if (editingJson) {
      // В режиме JSON редактора сравниваем текст JSON
      const hasChanges = jsonText !== JSON.stringify(script, null, 2);
      setHasUnsavedChanges(hasChanges);
    } else {
      // В визуальном редакторе сравниваем состояние
      const currentState = JSON.stringify(script);
      const hasChanges = currentState !== lastSavedStateRef.current;
      setHasUnsavedChanges(hasChanges);
    }
  }, [script, editingJson, jsonText]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z') {
        e.preventDefault();
        redo();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  const handleSave = async () => {
    // Сохранение теперь управляется через ref из ProjectEditor
    // Эта функция больше не используется напрямую
    if (onSave) await onSave();
  };

  const updateScript = (updater) => {
    const newScript = produce(script, updater);
    updateState(newScript);
    if (onChange) onChange(newScript);
  };

  const toggleStage = (stageIndex) => {
    setExpandedStages(prev => ({ ...prev, [stageIndex]: !prev[stageIndex] }));
  };

  const toggleVariant = (key) => {
    setExpandedVariants(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleStep = (key) => {
    setExpandedSteps(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const addStage = () => {
    updateScript(draft => {
      if (!draft.Main_Script) draft.Main_Script = { Stages: [] };
      const stageNumber = getNextStageNumber(draft);
      draft.Main_Script.Stages.push(createEmptyStage(stageNumber));
    });
  };

  const deleteStage = (stageIndex) => {
    if (!window.confirm('Удалить этап?')) return;
    updateScript(draft => {
      draft.Main_Script.Stages.splice(stageIndex, 1);
    });
  };

  const duplicateStage = (stageIndex) => {
    updateScript(draft => {
      const stage = draft.Main_Script.Stages[stageIndex];
      const newStageNumber = getNextStageNumber(draft);
      const cloned = cloneStage(stage, newStageNumber);
      draft.Main_Script.Stages.splice(stageIndex + 1, 0, cloned);
    });
  };

  const moveStage = (stageIndex, direction) => {
    const targetIndex = direction === 'up' ? stageIndex - 1 : stageIndex + 1;
    if (targetIndex < 0 || targetIndex >= script.Main_Script.Stages.length) return;
    
    updateScript(draft => {
      const stages = draft.Main_Script.Stages;
      [stages[stageIndex], stages[targetIndex]] = [stages[targetIndex], stages[stageIndex]];
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    updateScript(draft => {
      const stages = draft.Main_Script.Stages;
      const oldIndex = stages.findIndex(s => s.UUID === active.id);
      const newIndex = stages.findIndex(s => s.UUID === over.id);
        
      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = arrayMove(stages, oldIndex, newIndex);
        draft.Main_Script.Stages = reordered;
      }
    });
  };

  const handleStepDragEnd = (event, stageIndex, variantIndex) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    updateScript(draft => {
      const variant = draft.Main_Script.Stages[stageIndex].Stage_Vars[variantIndex];
      const steps = variant.Steps;
      const oldIndex = steps.findIndex(s => s.UUID === active.id);
      const newIndex = steps.findIndex(s => s.UUID === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = arrayMove(steps, oldIndex, newIndex);
        // Обновляем Step_Number для всех шагов согласно новому порядку
        reordered.forEach((step, index) => {
          step.Step_Number = String(index + 1);
        });
        variant.Steps = reordered;
      }
    });
  };

  const updateStageField = (stageIndex, field, value) => {
    updateScript(draft => {
      draft.Main_Script.Stages[stageIndex][field] = value;
    });
  };

  const updateVariantField = (stageIndex, variantIndex, field, value) => {
    updateScript(draft => {
      draft.Main_Script.Stages[stageIndex].Stage_Vars[variantIndex][field] = value;
    });
  };

  const addVariant = (stageIndex) => {
    updateScript(draft => {
      const stage = draft.Main_Script.Stages[stageIndex];
      if (!stage.Stage_Vars) stage.Stage_Vars = [];
      const stageNumber = parseInt(stage.Stage_Number) || stageIndex + 1;
      const variantNumber = stage.Stage_Vars.length + 1;
      stage.Stage_Vars.push(createEmptyStageVar(stageNumber, variantNumber));
    });
  };

  const deleteVariant = (stageIndex, variantIndex) => {
    if (!window.confirm('Удалить вариант?')) return;
    updateScript(draft => {
      draft.Main_Script.Stages[stageIndex].Stage_Vars.splice(variantIndex, 1);
    });
  };

  const addStep = (stageIndex, variantIndex) => {
    updateScript(draft => {
      const variant = draft.Main_Script.Stages[stageIndex].Stage_Vars[variantIndex];
      if (!variant.Steps) variant.Steps = [];
      const stageNumber = parseInt(draft.Main_Script.Stages[stageIndex].Stage_Number) || stageIndex + 1;
      const variantNumber = variantIndex + 1;
      const stepNumber = getNextStepNumber(variant);
      variant.Steps.push(createEmptyStep(stageNumber, variantNumber, stepNumber));
    });
  };

  const deleteStep = (stageIndex, variantIndex, stepIndex) => {
    if (!window.confirm('Удалить шаг?')) return;
    updateScript(draft => {
      draft.Main_Script.Stages[stageIndex].Stage_Vars[variantIndex].Steps.splice(stepIndex, 1);
    });
  };

  const duplicateStep = (stageIndex, variantIndex, stepIndex) => {
    updateScript(draft => {
      const variant = draft.Main_Script.Stages[stageIndex].Stage_Vars[variantIndex];
      const step = variant.Steps[stepIndex];
      const newStepNumber = getNextStepNumber(variant);
      const cloned = cloneStep(step, newStepNumber);
      variant.Steps.splice(stepIndex + 1, 0, cloned);
    });
  };

  const updateStepField = (stageIndex, variantIndex, stepIndex, field, value) => {
    updateScript(draft => {
      draft.Main_Script.Stages[stageIndex].Stage_Vars[variantIndex].Steps[stepIndex][field] = value;
    });
  };

  const addStepVar = (stageIndex, variantIndex, stepIndex) => {
    updateScript(draft => {
      const step = draft.Main_Script.Stages[stageIndex].Stage_Vars[variantIndex].Steps[stepIndex];
      if (!step.Step_Vars) step.Step_Vars = [];
      const stageNumber = parseInt(draft.Main_Script.Stages[stageIndex].Stage_Number) || stageIndex + 1;
      const varNumber = variantIndex + 1;
      const stepNumber = parseInt(step.Step_Number) || stepIndex + 1;
      const stepVarNumber = step.Step_Vars.length + 1;
      step.Step_Vars.push(createEmptyStepVar(stageNumber, varNumber, stepNumber, stepVarNumber));
    });
  };

  const deleteStepVar = (stageIndex, variantIndex, stepIndex, stepVarIndex) => {
    if (!window.confirm('Удалить вариант шага?')) return;
    updateScript(draft => {
      draft.Main_Script.Stages[stageIndex].Stage_Vars[variantIndex].Steps[stepIndex].Step_Vars.splice(stepVarIndex, 1);
    });
  };

  const updateStepVarField = (stageIndex, variantIndex, stepIndex, stepVarIndex, field, value) => {
    updateScript(draft => {
      const stepVar = draft.Main_Script.Stages[stageIndex].Stage_Vars[variantIndex].Steps[stepIndex].Step_Vars[stepVarIndex];
      if (field.startsWith('Task_Settings.')) {
        const taskField = field.split('.')[1];
        if (!stepVar.Task_Settings) stepVar.Task_Settings = {};
        stepVar.Task_Settings[taskField] = value;
      } else {
        stepVar[field] = value;
      }
    });
  };

  const updateStepVar = (stageIndex, variantIndex, stepIndex, stepVarIndex, newStepVar) => {
    updateScript(draft => {
      draft.Main_Script.Stages[stageIndex].Stage_Vars[variantIndex].Steps[stepIndex].Step_Vars[stepVarIndex] = newStepVar;
    });
  };

  const handleContextMenu = (e, item, index) => {
    e.preventDefault();
    setContextMenu({
      isOpen: true,
      position: { x: e.clientX, y: e.clientY },
      item,
      index
    });
  };

  const closeContextMenu = () => {
    setContextMenu({ isOpen: false, position: { x: 0, y: 0 }, item: null, index: null });
  };

  const handleNavigate = (uuid) => {
    setCurrentNavigationUUID(uuid);
    
    // Найти элемент в структуре скрипта и развернуть все родительские элементы
    const stages = script?.Main_Script?.Stages || [];
    
    // Поиск по этапам
    for (let stageIndex = 0; stageIndex < stages.length; stageIndex++) {
      const stage = stages[stageIndex];
      
      if (stage.UUID === uuid) {
        // Это этап - просто развернуть его
        setExpandedStages(prev => ({ ...prev, [stageIndex]: true }));
        break;
      }
      
      // Поиск по вариантам и шагам
      const variants = stage.Stage_Vars || [];
      for (let variantIndex = 0; variantIndex < variants.length; variantIndex++) {
        const variant = variants[variantIndex];
        const steps = variant.Steps || [];
        
        for (let stepIndex = 0; stepIndex < steps.length; stepIndex++) {
          const step = steps[stepIndex];
          
          if (step.UUID === uuid) {
            // Это шаг - развернуть этап, вариант и шаг
            const variantKey = `${stageIndex}-${variantIndex}`;
            const stepKey = `${variantKey}-${stepIndex}`;
            
            setExpandedStages(prev => ({ ...prev, [stageIndex]: true }));
            setExpandedVariants(prev => ({ ...prev, [variantKey]: true }));
            setExpandedSteps(prev => ({ ...prev, [stepKey]: true }));
            break;
          }
        }
      }
    }
    
    // Прокрутка к элементу с небольшой задержкой для анимации разворачивания
    setTimeout(() => {
      const element = document.getElementById(`element-${uuid}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
  };

  const updateBotRole = (value) => {
    updateScript(draft => {
      if (!draft.Settings) draft.Settings = {};
      draft.Settings.Bot_Role = value;
    });
  };

  const updateNotifications = (notifications) => {
    updateScript(draft => {
      if (!draft.Settings) draft.Settings = {};
      draft.Settings.Notifications = notifications;
    });
  };

  const updateIncidents = (incidents) => {
    updateScript(draft => {
      draft.Script_For_Incidents = incidents;
    });
  };

  const updateFollowups = (followups) => {
    updateScript(draft => {
      draft.Followups_For_Scripts = followups;
    });
  };

  const updateCoordinatingSettings = (settings) => {
    updateScript(draft => {
      draft.Coordinating_Settings = settings;
    });
  };

  // Управление степенью детализации отображения
  const collapseAll = () => {
    setExpandedStages({});
    setExpandedVariants({});
    setExpandedSteps({});
    setDetailLevel(0);
  };

  const expandAllStages = () => {
    const stages = script?.Main_Script?.Stages || [];
    const newExpanded = {};
    stages.forEach((_, idx) => {
      newExpanded[idx] = true;
    });
    setExpandedStages(newExpanded);
    setExpandedVariants({});
    setExpandedSteps({});
    setDetailLevel(1);
  };

  const expandToSteps = () => {
    const stages = script?.Main_Script?.Stages || [];
    const newStages = {};
    const newVariants = {};
    
    stages.forEach((stage, stageIdx) => {
      newStages[stageIdx] = true;
      stage.Stage_Vars?.forEach((_, variantIdx) => {
        const variantKey = `${stageIdx}-${variantIdx}`;
        newVariants[variantKey] = true;
      });
    });
    
    setExpandedStages(newStages);
    setExpandedVariants(newVariants);
    setExpandedSteps({});
    setDetailLevel(2);
  };

  const expandAll = () => {
    const stages = script?.Main_Script?.Stages || [];
    const newStages = {};
    const newVariants = {};
    const newSteps = {};
    
    stages.forEach((stage, stageIdx) => {
      newStages[stageIdx] = true;
      stage.Stage_Vars?.forEach((variant, variantIdx) => {
        const variantKey = `${stageIdx}-${variantIdx}`;
        newVariants[variantKey] = true;
        variant.Steps?.forEach((_, stepIdx) => {
          const stepKey = `${variantKey}-${stepIdx}`;
          newSteps[stepKey] = true;
        });
      });
    });
    
    setExpandedStages(newStages);
    setExpandedVariants(newVariants);
    setExpandedSteps(newSteps);
    setDetailLevel(3);
  };

  // Применяем изменения из JSON редактора при переключении обратно на визуальный редактор
  const handleToggleJsonEditor = (enable) => {
    if (enable) {
      // Переключаемся в JSON редактор - сохраняем текущее состояние
      setJsonText(JSON.stringify(script, null, 2));
      setEditingJson(true);
    } else {
      // Переключаемся обратно в визуальный редактор - применяем изменения
      try {
        const parsed = JSON.parse(jsonText);
        updateState(parsed);
        if (onChange) onChange(parsed);
        setEditingJson(false);
      } catch (err) {
        console.error('Invalid JSON:', err);
        alert('Невалидный JSON. Проверьте синтаксис перед переключением на визуальный редактор.');
      }
    }
  };

  if (editingJson) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium">JSON редактор скрипта</h3>
          <Button onClick={() => handleToggleJsonEditor(false)} variant="outline">
            Визуальный редактор
          </Button>
        </div>
        <AdvancedJsonEditor
          value={jsonText}
          onChange={(newValue) => {
            const jsonString = JSON.stringify(newValue, null, 2);
            setJsonText(jsonString);
            updateState(newValue);
            if (onChange) onChange(newValue);
            setHasUnsavedChanges(true);
          }}
          height="700px"
        />
        <div className="text-xs text-gray-500 flex items-center gap-2">
          <span className="text-green-600">✓</span>
          <span>Изменения автоматически сохраняются. Нажмите "Сохранить" вверху страницы для применения.</span>
        </div>
      </div>
    );
  }

  const stages = script?.Main_Script?.Stages || [];

  const tabs = [
    { id: 'main_script', label: 'Основной скрипт', icon: '📝' },
    { id: 'followups', label: 'Follow-Ups', icon: '�' },
    { id: 'incidents', label: 'Инциденты', icon: '⚠️' },
    { id: 'coordinating', label: 'Координация', icon: '�' },
    { id: 'settings', label: 'Настройки', icon: '⚙️' }
  ];

  return (
    <div className="relative pb-32">
      {activeTab === 'main_script' && (
        <ScriptNavigator 
          script={script} 
          onNavigate={handleNavigate}
          currentUUID={currentNavigationUUID}
        />
      )}

      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold">Конструктор скрипта</h3>
        <div className="flex gap-2">
          <Button onClick={() => handleToggleJsonEditor(true)} variant="outline" size="sm">
            <Edit2 size={16} className="mr-2" />
            JSON редактор
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex gap-2 -mb-px">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-all flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === 'settings' && (
            <SettingsEditor
              notifications={script?.Settings?.Notifications}
              onChange={updateNotifications}
            />
          )}

          {activeTab === 'incidents' && (
            <IncidentsEditor
              incidents={script?.Script_For_Incidents}
              onChange={updateIncidents}
            />
          )}

          {activeTab === 'followups' && (
            <FollowupsEditor
              followups={script?.Followups_For_Scripts}
              onChange={updateFollowups}
            />
          )}

          {activeTab === 'coordinating' && (
            <CoordinatingSettingsEditor
              settings={script?.Coordinating_Settings}
              onChange={updateCoordinatingSettings}
            />
          )}

          {activeTab === 'main_script' && (
            <div>
              {/* Панель управления детализацией */}
              <div className="mb-4 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Детализация</span>
                  <div className="flex bg-white border border-gray-300 rounded-lg p-0.5 gap-0.5">
                    {[
                      { level: 0, label: 'Свернуть', icon: <Minimize2 size={13} />, title: 'Свернуть всё', fn: collapseAll },
                      { level: 1, label: 'Этапы', icon: <List size={13} />, title: 'Только этапы', fn: expandAllStages },
                      { level: 2, label: 'До шагов', icon: <ListTree size={13} />, title: 'Этапы + варианты + шаги', fn: expandToSteps },
                      { level: 3, label: 'Всё', icon: <Maximize2 size={13} />, title: 'Развернуть полностью', fn: expandAll },
                    ].map(({ level, label, icon, title, fn }) => (
                      <button
                        key={level}
                        onClick={fn}
                        title={title}
                        className={`px-2.5 py-1 text-xs font-medium rounded-md flex items-center gap-1 transition-colors ${
                          detailLevel === level
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {icon}
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={addStage}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors flex items-center gap-1.5"
                  >
                    <Plus size={14} />
                    Добавить этап
                  </button>
                </div>
              </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={stages.map(s => s.UUID)} strategy={verticalListSortingStrategy}>
          <div className="space-y-6">
            <AnimatePresence>
              {stages.map((stage, stageIndex) => (
                <SortableStage 
                  key={stage.UUID} 
                  stage={stage} 
                  index={stageIndex}
                  onContextMenu={handleContextMenu}
                >
                  <div 
                    id={`element-${stage.UUID}`}
                    className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                  >
                    {/* Stage Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3 flex-1">
                        <button
                          onClick={() => toggleStage(stageIndex)}
                          className="text-gray-600 hover:text-gray-900 transition-colors"
                        >
                          {expandedStages[stageIndex] ? <ChevronDown size={24} /> : <ChevronRight size={24} />}
                        </button>
                        <div className="flex-1">
                          <input
                            type="text"
                            value={stage.Stage_Name || ''}
                            onChange={(e) => updateStageField(stageIndex, 'Stage_Name', e.target.value)}
                            className="text-xl font-bold text-gray-900 border-2 border-transparent hover:border-blue-300 focus:border-blue-500 rounded-lg px-3 py-1 w-full transition-all"
                            placeholder="Название этапа"
                          />
                          <span className="text-sm text-gray-500 ml-3">Этап #{stage.Stage_Number}</span>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <button
                          onClick={() => addVariant(stageIndex)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Добавить вариант"
                        >
                          <Plus size={20} />
                        </button>
                        <button
                          onClick={() => deleteStage(stageIndex)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Удалить этап"
                        >
                          <Trash2 size={20} />
                        </button>
                      </div>
                    </div>

                    {expandedStages[stageIndex] && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="space-y-4"
                      >
                        {/* Stage Fields */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Описание этапа
                            </label>
                            <textarea
                              value={stage.Stage_Description || ''}
                              onChange={(e) => updateStageField(stageIndex, 'Stage_Description', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              rows={2}
                              placeholder="Краткое описание этапа..."
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Пропустить если
                            </label>
                            <textarea
                              value={stage.Skip_If || ''}
                              onChange={(e) => updateStageField(stageIndex, 'Skip_If', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              rows={2}
                              placeholder="Условия для пропуска этапа..."
                            />
                          </div>
                        </div>

                        {/* Stage Variants */}
                        <div className="space-y-3 mt-4">
                          {stage.Stage_Vars?.map((variant, variantIndex) => {
                            const variantKey = `${stageIndex}-${variantIndex}`;
                            return (
                              <div key={variant.UUID} className="bg-white border-l-4 border-blue-500 rounded-lg p-4 shadow-sm">
                                <div className="flex items-center justify-between mb-3">
                                  <button
                                    onClick={() => toggleVariant(variantKey)}
                                    className="flex items-center gap-2 text-gray-700 font-medium hover:text-blue-600"
                                  >
                                    {expandedVariants[variantKey] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                                    <span>
                                      {stage.Stage_Vars.length > 1 
                                        ? `Вариант ${variantIndex + 1}${variant.Stage_Name ? `: ${variant.Stage_Name}` : ''}` 
                                        : (variant.Stage_Name || 'Вариант')
                                      }
                                    </span>
                                  </button>
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => addStep(stageIndex, variantIndex)}
                                      className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                      title="Добавить шаг"
                                    >
                                      <Plus size={16} />
                                    </button>
                                    <button
                                      onClick={() => deleteVariant(stageIndex, variantIndex)}
                                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                                      title="Удалить вариант"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </div>
                                </div>

                                {expandedVariants[variantKey] && (
                                  <>
                                    {/* Variant Fields */}
                                    <div className="grid grid-cols-2 gap-4 mb-4">
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                          Название варианта (Stage_Name)
                                        </label>
                                        <input
                                          type="text"
                                          value={variant.Stage_Name || ''}
                                          onChange={(e) => updateVariantField(stageIndex, variantIndex, 'Stage_Name', e.target.value)}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                          placeholder="Название варианта..."
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                          Выбрать если (Choose_If)
                                        </label>
                                        <input
                                          type="text"
                                          value={variant.Choose_If || ''}
                                          onChange={(e) => updateVariantField(stageIndex, variantIndex, 'Choose_If', e.target.value)}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                          placeholder="Условие выбора варианта..."
                                        />
                                      </div>
                                    </div>

                                  <DndContext 
                                    sensors={sensors} 
                                    collisionDetection={closestCenter} 
                                    onDragEnd={(event) => handleStepDragEnd(event, stageIndex, variantIndex)}
                                  >
                                    <SortableContext items={variant.Steps?.map(s => s.UUID) || []} strategy={verticalListSortingStrategy}>
                                      <div className="space-y-3">
                                        {/* Steps */}
                                        {variant.Steps?.map((step, stepIndex) => {
                                          const stepKey = `${variantKey}-${stepIndex}`;
                                          return (
                                            <SortableStep key={step.UUID} step={step}>
                                              <div 
                                                id={`element-${step.UUID}`}
                                                className="bg-gray-50 border border-gray-200 rounded-lg p-4"
                                              >
                                          {/* Step header */}
                                          <div className="flex items-start gap-2 mb-2">
                                            <button
                                              onClick={() => toggleStep(stepKey)}
                                              className="mt-0.5 text-gray-500 hover:text-blue-600 shrink-0 transition-colors"
                                            >
                                              {expandedSteps[stepKey] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                            </button>

                                            {editingStep.key === stepKey ? (
                                              <div className="flex-1 min-w-0 space-y-1.5">
                                                <input
                                                  autoFocus
                                                  type="text"
                                                  value={editingStep.name}
                                                  onChange={(e) => setEditingStep(s => ({ ...s, name: e.target.value }))}
                                                  onKeyDown={(e) => { if (e.key === 'Escape') cancelEditStep(); }}
                                                  className="w-full px-2 py-1 text-sm border border-blue-400 rounded outline-none focus:ring-1 focus:ring-blue-200"
                                                  placeholder="Название шага"
                                                />
                                                <textarea
                                                  value={editingStep.description}
                                                  onChange={(e) => setEditingStep(s => ({ ...s, description: e.target.value }))}
                                                  className="w-full px-2 py-1 text-xs border border-blue-200 rounded outline-none focus:ring-1 focus:ring-blue-200"
                                                  rows={2}
                                                  placeholder="Описание шага (optional)"
                                                />
                                                <div className="flex gap-1">
                                                  <button
                                                    onClick={() => saveEditStep(stageIndex, variantIndex, stepIndex)}
                                                    className="flex items-center gap-1 px-2 py-0.5 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                                                  >
                                                    <Check size={12} /> Сохранить
                                                  </button>
                                                  <button
                                                    onClick={cancelEditStep}
                                                    className="flex items-center gap-1 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                                  >
                                                    <X size={12} /> Отмена
                                                  </button>
                                                </div>
                                              </div>
                                            ) : (
                                              <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-1.5">
                                                  <span className="text-sm font-medium text-gray-700">Шаг {step.Step_Number}: {step.Step_Name}</span>
                                                  <button
                                                    onClick={(e) => startEditStep(e, stepKey, step)}
                                                    className="shrink-0 text-gray-400 hover:text-blue-500 p-0.5 rounded transition-colors"
                                                    title="Редактировать название и описание"
                                                  >
                                                    <Edit2 size={12} />
                                                  </button>
                                                </div>
                                                {step.Step_Description && (
                                                  <p className="mt-0.5 text-xs text-gray-500 italic">{step.Step_Description}</p>
                                                )}
                                              </div>
                                            )}

                                            <div className="flex gap-1 ml-auto shrink-0">
                                              <button
                                                onClick={() => addStepVar(stageIndex, variantIndex, stepIndex)}
                                                className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                                                title="Добавить вариант шага"
                                              >
                                                <Plus size={14} />
                                              </button>
                                              <button
                                                onClick={() => duplicateStep(stageIndex, variantIndex, stepIndex)}
                                                className="p-1 text-green-600 hover:bg-green-50 rounded"
                                                title="Дублировать шаг"
                                              >
                                                <Copy size={14} />
                                              </button>
                                              <button
                                                onClick={() => deleteStep(stageIndex, variantIndex, stepIndex)}
                                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                                                title="Удалить шаг"
                                              >
                                                <Trash2 size={14} />
                                              </button>
                                            </div>
                                          </div>

                                          {expandedSteps[stepKey] && (
                                            <div className="mt-3 space-y-2 text-sm">
                                              {/* Skip_If for step */}
                                              <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                  Пропустить если (Skip_If)
                                                </label>
                                                <input
                                                  type="text"
                                                  value={step.Skip_If || ''}
                                                  onChange={(e) => updateStepField(stageIndex, variantIndex, stepIndex, 'Skip_If', e.target.value)}
                                                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                  placeholder="Условие для пропуска шага..."
                                                />
                                              </div>
                                              {/* Step Vars */}
                                              {step.Step_Vars?.map((stepVar, stepVarIndex) => (
                                                <StepVarEditor
                                                  key={stepVar.Var_UUID}
                                                  stepVar={stepVar}
                                                  onChange={(newStepVar) => updateStepVar(stageIndex, variantIndex, stepIndex, stepVarIndex, newStepVar)}
                                                  onDelete={() => deleteStepVar(stageIndex, variantIndex, stepIndex, stepVarIndex)}
                                                />
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                            </SortableStep>
                                          );
                                        })}
                                      </div>
                                    </SortableContext>
                                  </DndContext>
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </div>
                </SortableStage>
              ))}
            </AnimatePresence>
          </div>
        </SortableContext>
      </DndContext>

              {/* Add Stage Button */}
              <motion.button
                onClick={addStage}
                className="mt-6 w-full py-4 border-2 border-dashed border-blue-300 rounded-xl text-blue-600 font-semibold hover:bg-blue-50 hover:border-blue-500 transition-all flex items-center justify-center gap-2"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <Plus size={20} />
                <span>Добавить этап</span>
              </motion.button>

              {/* Context Menu */}
              {contextMenu.item && (
                <ContextMenu
                  isOpen={contextMenu.isOpen}
                  position={contextMenu.position}
                  onClose={closeContextMenu}
                  onCopy={() => console.log('Copy', contextMenu.item)}
                  onDuplicate={() => duplicateStage(contextMenu.index)}
                  onDelete={() => deleteStage(contextMenu.index)}
                  onMoveUp={() => moveStage(contextMenu.index, 'up')}
                  onMoveDown={() => moveStage(contextMenu.index, 'down')}
                  canMoveUp={contextMenu.index > 0}
                  canMoveDown={contextMenu.index < stages.length - 1}
                />
              )}
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Floating Buttons */}
      <FloatingSaveButton 
        hasChanges={hasUnsavedChanges}
        isSaving={isSaving}
        onSave={handleSave}
      />

      <FloatingUndoButton
        canUndo={canUndo}
        canRedo={canRedo}
        onUndo={undo}
        onRedo={redo}
      />
    </div>
  );
});

export default ImprovedScriptEditor;
